import React from 'react';
import styles from '../ProjectEditForm.module.css';
import notesStyles from './NotesSettingsPanel.module.css';

function normalizeDraftNotesConfig(notes) {
    const n = notes ?? {};
    const enabled = Boolean(n.enabled);
    const label = typeof n.label === 'string' ? n.label : '';
    const placeholder = typeof n.placeholder === 'string' ? n.placeholder : '';
    const instructions = typeof n.instructions === 'string' ? n.instructions : '';
    const maxLength =
        Number.isFinite(n.maxLength) && n.maxLength >= 100 && n.maxLength <= 50000
            ? n.maxLength
            : 5000;
    return { enabled, label, placeholder, instructions, maxLength };
}

function NotesSettingsPanel({
    notesConfig,
    setNotesConfig,
    settingsLocked = false,
}) {
    const cfg = normalizeDraftNotesConfig(notesConfig);
    const detailsDisabled = !cfg.enabled || settingsLocked;

    return (
        <section
            id="project-section-notes"
            className={styles.settingsScrollAnchor}
            aria-label="Notes"
        >
            <div className={styles.panel}>
                <h4 className={styles.panelTitle}>Notes (project)</h4>
                {settingsLocked && (
                    <p className={styles.panelLockBanner}>
                        Published: return to draft to edit this section.
                    </p>
                )}
                <div className={settingsLocked ? styles.panelDisabled : undefined}>
                    <label className={notesStyles.checkboxRow} htmlFor="notes-cfg-enabled">
                        <input
                            id="notes-cfg-enabled"
                            type="checkbox"
                            checked={cfg.enabled}
                            disabled={settingsLocked}
                            onChange={(e) => setNotesConfig((prev) => ({
                                ...normalizeDraftNotesConfig(prev),
                                enabled: Boolean(e.target.checked),
                            }))}
                        />
                        <span className={notesStyles.checkboxLabel}>
                            Enable per-slide notes in the annotation viewer
                        </span>
                    </label>

                    <p className={notesStyles.intro}>
                        Adds a Notes button to the viewer top bar so each annotator can
                        record free-text notes per slide.
                    </p>

                    <div
                        className={notesStyles.detailFields}
                        aria-disabled={detailsDisabled ? 'true' : 'false'}
                    >
                        <div className={notesStyles.formStack}>
                            <div className={notesStyles.gridTwo}>
                                <div className={notesStyles.field}>
                                    <label
                                        className={notesStyles.fieldLabel}
                                        htmlFor="notes-cfg-label"
                                    >
                                        Button label
                                    </label>
                                    <input
                                        id="notes-cfg-label"
                                        className={notesStyles.input}
                                        value={cfg.label}
                                        disabled={detailsDisabled}
                                        onChange={(e) => setNotesConfig((prev) => ({
                                            ...normalizeDraftNotesConfig(prev),
                                            label: e.target.value,
                                        }))}
                                        placeholder="Notes"
                                    />
                                </div>
                                <div className={notesStyles.field}>
                                    <label
                                        className={notesStyles.fieldLabel}
                                        htmlFor="notes-cfg-max-length"
                                    >
                                        Max length
                                    </label>
                                    <input
                                        id="notes-cfg-max-length"
                                        className={`${notesStyles.input} ${notesStyles.numberInput}`}
                                        type="number"
                                        min={100}
                                        max={50000}
                                        value={cfg.maxLength}
                                        disabled={detailsDisabled}
                                        onChange={(e) => setNotesConfig((prev) => ({
                                            ...normalizeDraftNotesConfig(prev),
                                            maxLength: Number(e.target.value || 5000),
                                        }))}
                                    />
                                </div>
                            </div>

                            <div className={notesStyles.field}>
                                <label
                                    className={notesStyles.fieldLabel}
                                    htmlFor="notes-cfg-placeholder"
                                >
                                    Placeholder
                                </label>
                                <input
                                    id="notes-cfg-placeholder"
                                    className={notesStyles.input}
                                    value={cfg.placeholder}
                                    disabled={detailsDisabled}
                                    onChange={(e) => setNotesConfig((prev) => ({
                                        ...normalizeDraftNotesConfig(prev),
                                        placeholder: e.target.value,
                                    }))}
                                    placeholder="Add notes…"
                                />
                            </div>

                            <div className={notesStyles.field}>
                                <label
                                    className={notesStyles.fieldLabel}
                                    htmlFor="notes-cfg-instructions"
                                >
                                    Instructions
                                </label>
                                <textarea
                                    id="notes-cfg-instructions"
                                    className={notesStyles.textarea}
                                    value={cfg.instructions}
                                    disabled={detailsDisabled}
                                    onChange={(e) => setNotesConfig((prev) => ({
                                        ...normalizeDraftNotesConfig(prev),
                                        instructions: e.target.value,
                                    }))}
                                    placeholder="Optional guidance shown in the notes popover."
                                />
                                <p className={notesStyles.fieldHint}>
                                    Shown in the ⓘ popover next to the notes textarea.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default React.memo(NotesSettingsPanel);
